import React from 'react';
import { Grid, Skeleton, Box, Stack, Card, CardContent, useTheme } from '@mui/material';

interface SkeletonCardProps {
  children?: React.ReactNode;
  height?: string | number;
  sx?: any;
  contentSx?: any;
}

const AccountsDashboardPageSkeleton = () => {
  const theme = useTheme();

  const GRID_SPACING = { xs: 1, sm: 2 };

  const SkeletonCard = ({ children, height, sx = {}, contentSx = {} }: SkeletonCardProps) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 1.5, md: 2 },
          '&:last-child': { pb: { xs: 1.5, md: 2 } },
          ...contentSx
        }}
      >
        {children ? children : <Skeleton variant="rectangular" height={height || '100%'} sx={{ borderRadius: 2 }} />}
      </CardContent>
    </Card>
  );

  const StatBlock = () => (
    <Stack spacing={1} justifyContent="center" height="100%">
      <Skeleton variant="text" width="40%" height={40} />
      <Skeleton variant="text" width="80%" height={40} />
      <Box display="flex" gap={1}>
        <Skeleton variant="text" width="30%" height={20} />
        <Skeleton variant="text" width="40%" height={20} />
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={{ xs: 1, md: 2 }}
      >
        <Box width={{ xs: '100%', sm: 'auto' }}>
          <Skeleton variant="text" width={240} height={60} />
        </Box>

        <Box display="flex" alignItems="center" gap={2} mt={{ xs: 1, sm: 0 }} width={{ xs: '100%', sm: 'auto' }}>
          <Skeleton variant="text" width={50} height={30} />

          <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1, flex: { xs: 1, sm: 'none' } }} />
        </Box>
      </Box>
      <Grid container spacing={GRID_SPACING} mb={{ xs: 2, md: 3 }}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={6} sm={6} md={3} key={`top-${index}`}>
            <SkeletonCard>
              <StatBlock />
            </SkeletonCard>
          </Grid>
        ))}
      </Grid>
      <Stack spacing={{ xs: 2, md: 3 }}>
        <SkeletonCard sx={{ minHeight: 480 }}>
          <Stack spacing={1}>
            <Box mb={{ xs: 2, md: 3 }}>
              <Box display="flex" alignItems="center" mb={{ xs: 1.5, sm: 2 }}>
                <Skeleton variant="circular" width={28} height={28} sx={{ mr: 1.5 }} />
                <Skeleton variant="text" width={120} height={32} />
                <Skeleton variant="circular" width={16} height={16} sx={{ ml: 1 }} />
              </Box>

              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="flex-start" gap={{ xs: 1.5, sm: 2 }}>
                <Skeleton
                  variant="rectangular"
                  sx={{
                    borderRadius: 1.5,
                    width: { xs: '40%', sm: 320 },
                    height: { xs: 30, sm: 40 }
                  }}
                />
                <Skeleton
                  variant="rectangular"
                  sx={{
                    borderRadius: 1.5,
                    width: { xs: '70%', sm: 280 },
                    height: { xs: 30, sm: 40 }
                  }}
                />
              </Box>
            </Box>

            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={45} sx={{ borderRadius: 1.5 }} />
            ))}
          </Stack>
        </SkeletonCard>
      </Stack>
    </Box>
  );
};

export default AccountsDashboardPageSkeleton;
